import type { ReactNode } from 'react'

import { Checkbox } from '@/components/ui/checkbox'

export interface ToolSelectorSelectAllProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onDeselectAll: () => void
}

/**
 * Select All toggle button for the tool selector dropdown.
 * Shows a checkbox with indeterminate state when some items are selected.
 */
export const ToolSelectorSelectAll = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
}: ToolSelectorSelectAllProps): ReactNode => {
  const isAllSelected = totalCount > 0 && selectedCount === totalCount
  const isSomeSelected = selectedCount > 0 && selectedCount < totalCount

  const handleToggle = (): void => {
    if (isAllSelected) {
      onDeselectAll()
      return
    }
    onSelectAll()
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      className="tool-selector-select-all flex w-full cursor-default items-center gap-2 rounded-sm px-3 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground"
    >
      <Checkbox
        checked={isAllSelected || (isSomeSelected && 'indeterminate')}
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span className="font-medium">
        Select All ({selectedCount}/{totalCount})
      </span>
    </button>
  )
}

ToolSelectorSelectAll.displayName = 'ToolSelectorSelectAll'
